import React from 'react'
import { useIntl } from 'react-intl'
import { css } from '@emotion/css'
import { Text } from '@contrast-security-inc/design-system-typography'
import { BasicDialogBody, Dialog } from '../../legacy-design-system/modal'
import { logSuccess, logError } from '../toaster'
import { useSourceNameFormContext } from './source-name-form-context'
import { useSourceNames } from './api/use-source-names'
import { FormValidator } from '../../legacy-design-system/form'
import { SourceNameForm } from './source-name-form'
import { useCreateSourceName } from './api/use-create-source-name'
import { buildSourceNameVariables } from './shared'
import { SourceNameDialogProps } from './manage-source-name-dialog'

export const addSourceNameDialogTestId = 'add-source-name-dialog'

export const AddSourceNameDialog: React.FC<
  Omit<SourceNameDialogProps, 'sourceIP' | 'sourceName'>
> = ({ organizationUuid, isOpen, closeDialog, refresh }) => {
  const { formatMessage } = useIntl()
  const {
    isLoading,
    data: sourceNames,
    error
  } = useSourceNames(organizationUuid, { enabled: isOpen })
  const { state } = useSourceNameFormContext()
  const { mutate: addSourceName } = useCreateSourceName({
    onSuccess: () => {
      logSuccess(formatMessage({ id: 'detail_sourceNames_saveSuccessMessage' }))
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      refresh()
      closeDialog()
    },
    onError: (_error) => {
      logError(formatMessage({ id: 'detail_sourceNames_saveErrorMessage' }))
    }
  })

  if (error) {
    throw new Error(error.message)
  }

  return (
    <Dialog
      testId={addSourceNameDialogTestId}
      size="large"
      isOpen={isOpen}
      onCancel={closeDialog}
      renderTitle={() => (
        <Text
          font="bodyStrong"
          color="black"
          className={css`
            margin-right: 5px;
          `}
        >
          {formatMessage({ id: 'action_ipsManagement_addSourceName' })}
        </Text>
      )}
    >
      <FormValidator value={state}>
        {({ getFormFieldProps, formIsValid, setCustomValidity }) => {
          return isLoading ? (
            <></>
          ) : (
            <BasicDialogBody
              body={
                <SourceNameForm
                  setCustomValidity={setCustomValidity}
                  organizationID={organizationUuid}
                  getFormFieldProps={getFormFieldProps}
                  existingSourceNames={sourceNames}
                />
              }
              onConfirm={() =>
                addSourceName({
                  organizationUuid,
                  ...buildSourceNameVariables(state)
                })
              }
              confirmButtonText={
                <Text testId="add-source-name" font="body" color="white">
                  {formatMessage({ id: 'action_add' })}
                </Text>
              }
              disableConfirmButton={!formIsValid || state.name.length === 0}
              cancelButtonText={
                <Text font="body" color="neutral100">
                  {formatMessage({ id: 'action_cancel' })}
                </Text>
              }
              onCancel={closeDialog}
            />
          )
        }}
      </FormValidator>
    </Dialog>
  )
}
